import React from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Grid,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { AlertCircle } from 'lucide-react';
import { NameEmailAvatar } from '../../../../../../../../../ui';
import { PieCartWithStats } from '../../../../../../../../../ui/components/Common/Charts/PieCartWithStats';
import { BEIResultsData } from '../../AssessmentResultsDetails';

interface BEIAttribute {
  name: string;
  score: number;
  competencyLevel: 'Expert' | 'Proficient' | 'Developing' | 'Basic';
  evidenceCount: number;
}

interface SummaryStats {
  beiAttributes: BEIAttribute[];
  overallCompetencyScore: number;
  situationalResponses: number;
  starFrameworkAdherence: number;
  totalBehavioralExamples: number;
  totalQuestions: number;
  totalFollowUps: number;
  averageScore: number;
  attributesCount: number;
}

interface SummaryProps {
  assessmentData: any;
  beiResults: BEIResultsData[] | null;
  loading: boolean;
  error: string | null;
}

export const Summary: React.FC<SummaryProps> = ({
  assessmentData,
  beiResults,
  loading,
  error,
}) => {
  // Extract candidate information
  const getCandidateInfo = () => {
    const firstName = assessmentData?.user?.firstName || '';
    const lastName = assessmentData?.user?.lastName || '';
    const email = assessmentData?.user?.email || '';
    const fullName =
      firstName && lastName
        ? `${firstName} ${lastName}`
        : firstName || lastName || 'Candidate Name';

    return {
      name: fullName,
      email: email,
      company:
        assessmentData?.assessment?.jobPosition || 'Position Not Specified',
      date: assessmentData?.assessment?.creationDateTime
        ? new Date(
          assessmentData.assessment.creationDateTime * 1000,
        ).toLocaleDateString()
        : assessmentData?.startTime
          ? new Date(assessmentData.startTime * 1000).toLocaleDateString()
          : new Date().toLocaleDateString(),
      avatar: '',
    };
  };

  const getScoreHexColor = (score: number) => {
    if (score >= 80) return '#10B981'; // Emerald-500
    if (score >= 60) return '#3B82F6'; // Blue-500
    if (score >= 40) return '#F59E0B'; // Amber-500
    return '#EF4444'; // Red-500
  };

  const candidateInfo = getCandidateInfo();

  const summaryStats = React.useMemo<SummaryStats | null>(() => {
    if (!beiResults || beiResults.length === 0) return null;

    const beiAttributes = beiResults.map((result) => ({
      name: result.attribute,
      score: result.score,
      competencyLevel: (result.score >= 85
        ? 'Expert'
        : result.score >= 70
          ? 'Proficient'
          : result.score >= 55
            ? 'Developing'
            : 'Basic') as 'Expert' | 'Proficient' | 'Developing' | 'Basic',
      evidenceCount: result.questionAnswer.length,
    }));

    const overallCompetencyScore = Math.round(
      beiResults.reduce((sum, result) => sum + result.score, 0) /
        beiResults.length,
    );

    const starFrameworkAdherence =
      beiResults.reduce((sum, result) => {
        const hasStarComponents = result.questionAnswer.filter(
          (qa) =>
            qa?.answer &&
            qa.answer.toLowerCase().includes('situation') &&
            qa.answer.toLowerCase().includes('task') &&
            qa.answer.toLowerCase().includes('action') &&
            qa.answer.toLowerCase().includes('result'),
        ).length;
        return sum + (hasStarComponents / result.questionAnswer.length) * 100;
      }, 0) / beiResults.length;

    const totalBehavioralExamples = beiResults.reduce(
      (sum, result) => sum + result.questionAnswer.length,
      0,
    );

    const situationalResponses = beiResults.reduce(
      (sum, result) =>
        sum +
        result.questionAnswer.filter(
          (qa) =>
            qa?.answer &&
            (qa.answer.toLowerCase().includes('situation') ||
              qa.answer.toLowerCase().includes('scenario') ||
              qa.answer.toLowerCase().includes('example')),
        ).length,
      0,
    );

    return {
      beiAttributes,
      overallCompetencyScore,
      situationalResponses,
      starFrameworkAdherence,
      totalBehavioralExamples,
      totalQuestions: totalBehavioralExamples,
      totalFollowUps: beiResults.reduce(
        (sum, result) => sum + result.followupCount,
        0,
      ),
      averageScore: overallCompetencyScore,
      attributesCount: beiAttributes.length,
    };
  }, [beiResults]);

  // Handle loading state
  if (loading) {
    return (
      <Box
        sx={{
          width: '100%',
          minHeight: 400,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Stack spacing={2} alignItems="center">
          <CircularProgress color="primary" />
          <Typography variant="body2" color="text.secondary">
            Loading assessment summary...
          </Typography>
        </Stack>
      </Box>
    );
  }

  // Handle error state
  if (error) {
    return (
      <Box
        sx={{
          width: '100%',
          minHeight: 400,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Paper
          elevation={0}
          sx={{
            p: 4,
            border: 1,
            borderColor: 'error.light',
            bgcolor: 'error.50',
            textAlign: 'center',
            maxWidth: 420,
            borderRadius: 2,
          }}
        >
          <Stack spacing={2} alignItems="center">
            <AlertCircle size={32} color="#dc2626" />
            <Typography variant="subtitle1" fontWeight={700} color="error.dark">
              Error Loading Summary
            </Typography>
            <Typography variant="body2" color="error.main">
              {error}
            </Typography>
            <Button
              variant="contained"
              color="error"
              size="small"
              onClick={() => window.location.reload()}
            >
              Retry
            </Button>
          </Stack>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ flex: 1, overflowY: 'auto' }}>
        <Stack spacing={3}>
          {/* User Header Section */}
          <Paper
            elevation={0}
            sx={{
              p: 2,
              borderRadius: 2,
              border: 1,
              borderColor: 'divider',
              bgcolor: 'background.paper',
            }}
          >
            <Stack direction="row" spacing={2} alignItems="center">
              <NameEmailAvatar
                name={candidateInfo.name}
                email={candidateInfo.email}
                avatar={candidateInfo.avatar}
              />
              <Box>
                <Typography variant="body2" fontWeight={600} color="text.secondary">
                  {candidateInfo.company}
                </Typography>
                <Typography variant="caption" color="text.disabled">
                  {candidateInfo.date}
                </Typography>
              </Box>
            </Stack>
          </Paper>

          {/* Grid Layout for Charts */}
          <Grid container spacing={3} sx={{ pb: 3 }}>
            {/* Technical Skills Section */}
            {assessmentData?.skills && assessmentData.skills.length > 0 && (
              <Grid item xs={12} lg={6}>
                <Stack spacing={2}>
                  <PieCartWithStats
                    label="Technical Skills"
                    type="bar"
                    hideToggles={true}
                    maxValue={100}
                    legendWidth="w-[30%]"
                    barWidthOverride="20%"
                    data={{
                      Week: assessmentData.skills.map((skill: any) => ({
                        label: skill.skillName,
                        value:
                          skill.totalCount > 0
                            ? Math.round(
                              (skill.correctCount / skill.totalCount) * 100,
                            )
                            : 0,
                      })),
                    }}
                    colors={assessmentData.skills.map((skill: any) =>
                      getScoreHexColor(
                        skill.totalCount > 0
                          ? Math.round(
                            (skill.correctCount / skill.totalCount) * 100,
                          )
                          : 0,
                      ),
                    )}
                  />

                  {/* Technical Skills Summary Stats */}
                  <Grid container spacing={1.5}>
                    <Grid item xs={4}>
                      <Paper
                        elevation={0}
                        sx={{
                          p: 1.5,
                          textAlign: 'center',
                          borderRadius: 2,
                          bgcolor: 'primary.50',
                          border: 1,
                          borderColor: 'primary.100',
                        }}
                      >
                        <Typography
                          variant="h6"
                          fontWeight={700}
                          color="primary.dark"
                          lineHeight={1.2}
                        >
                          {assessmentData.skills.reduce(
                            (sum: number, skill: any) => sum + skill.totalCount,
                            0,
                          )}
                        </Typography>
                        <Typography
                          variant="caption"
                          fontWeight={700}
                          color="primary.main"
                          textTransform="uppercase"
                          display="block"
                          sx={{ fontSize: '0.65rem' }}
                        >
                          Questions
                        </Typography>
                      </Paper>
                    </Grid>

                    <Grid item xs={4}>
                      <Paper
                        elevation={0}
                        sx={{
                          p: 1.5,
                          textAlign: 'center',
                          borderRadius: 2,
                          bgcolor: 'success.50',
                          border: 1,
                          borderColor: 'success.100',
                        }}
                      >
                        <Typography
                          variant="h6"
                          fontWeight={700}
                          color="success.dark"
                          lineHeight={1.2}
                        >
                          {assessmentData.skills.reduce(
                            (sum: number, skill: any) => sum + skill.correctCount,
                            0,
                          )}
                        </Typography>
                        <Typography
                          variant="caption"
                          fontWeight={700}
                          color="success.main"
                          textTransform="uppercase"
                          display="block"
                          sx={{ fontSize: '0.65rem' }}
                        >
                          Correct
                        </Typography>
                      </Paper>
                    </Grid>

                    <Grid item xs={4}>
                      <Paper
                        elevation={0}
                        sx={{
                          p: 1.5,
                          textAlign: 'center',
                          borderRadius: 2,
                          bgcolor: 'grey.50',
                          border: 1,
                          borderColor: 'grey.200',
                        }}
                      >
                        <Typography
                          variant="h6"
                          fontWeight={700}
                          color="text.primary"
                          lineHeight={1.2}
                        >
                          {Math.round(
                            assessmentData.skills.reduce(
                              (sum: number, skill: any) =>
                                sum + skill.timeTakenForSkill / skill.totalCount,
                              0,
                            ) / assessmentData.skills.length,
                          )}
                          s
                        </Typography>
                        <Typography
                          variant="caption"
                          fontWeight={700}
                          color="text.secondary"
                          textTransform="uppercase"
                          display="block"
                          sx={{ fontSize: '0.65rem' }}
                        >
                          Avg Time
                        </Typography>
                      </Paper>
                    </Grid>
                  </Grid>
                </Stack>
              </Grid>
            )}

            {/* Behavioral Assessment Section */}
            {summaryStats && (
              <Grid item xs={12} lg={6}>
                <Stack spacing={2}>
                  <PieCartWithStats
                    label="Behavioral Assessment"
                    type="bar"
                    hideToggles={true}
                    maxValue={100}
                    legendWidth="w-[30%]"
                    barWidthOverride="20%"
                    data={{
                      Week: summaryStats.beiAttributes.map((attr: any) => ({
                        label: attr.name,
                        value: attr.score,
                      })),
                    }}
                    colors={summaryStats.beiAttributes.map((attr: any) =>
                      getScoreHexColor(attr.score),
                    )}
                  />

                  {/* Behavioral Metric Cards */}
                  <Grid container spacing={1.5}>
                    <Grid item xs={6}>
                      <Paper
                        elevation={0}
                        sx={{
                          p: 2,
                          borderRadius: 2,
                          bgcolor: 'primary.50',
                          border: 1,
                          borderColor: 'primary.100',
                        }}
                      >
                        <Typography
                          variant="h6"
                          fontWeight={700}
                          color="primary.dark"
                          lineHeight={1.2}
                        >
                          {summaryStats.totalBehavioralExamples}
                        </Typography>
                        <Typography
                          variant="caption"
                          fontWeight={600}
                          color="primary.main"
                          textTransform="uppercase"
                          display="block"
                          sx={{ fontSize: '0.65rem', mt: 0.5 }}
                        >
                          Responses provided
                        </Typography>
                      </Paper>
                    </Grid>

                    <Grid item xs={6}>
                      <Paper
                        elevation={0}
                        sx={{
                          p: 2,
                          borderRadius: 2,
                          bgcolor: 'success.50',
                          border: 1,
                          borderColor: 'success.100',
                        }}
                      >
                        <Typography
                          variant="h6"
                          fontWeight={700}
                          color="success.dark"
                          lineHeight={1.2}
                        >
                          {Math.round(summaryStats.starFrameworkAdherence)}%
                        </Typography>
                        <Typography
                          variant="caption"
                          fontWeight={600}
                          color="success.main"
                          textTransform="uppercase"
                          display="block"
                          sx={{ fontSize: '0.65rem', mt: 0.5 }}
                        >
                          STAR Adherence
                        </Typography>
                      </Paper>
                    </Grid>
                  </Grid>
                </Stack>
              </Grid>
            )}
          </Grid>
        </Stack>
      </Box>
    </Box>
  );
};
